import { v4 as uuid } from 'uuid';
import type { ReroutingTransitionValid } from './ReroutingTransitionValid.types.tsx';
import type { PortInfo, DiagramDirectTransition, DiagramSharedTransition } from './toPortInfo.tsx';
import type { ProposedTransitionValid } from './useTransitionProposals.tsx';

export function createTransitionFromProposed(
	validProposedTransition: ProposedTransitionValid,
	ports: Record<string, PortInfo>,
): DiagramDirectTransition {
	const { startPortKey, endPortKey } = validProposedTransition;

	const startPort = ports[startPortKey];
	const endPort = ports[endPortKey];

	return {
		id: uuid(),
		type: 'direct',
		icons: [],
		label: '',
		source: startPort.statusId,
		sourceIndex: startPort.portIndex,
		target: endPort.statusId,
		targetIndex: endPort.portIndex,
	};
}

export function updateTransitionAfterReroute(
	reroutingTransition: ReroutingTransitionValid,
	ports: Record<string, PortInfo>,
): DiagramDirectTransition | DiagramSharedTransition {
	const { transition, side, originalPortKey, proposedPortKey } = reroutingTransition;

	const proposedPort = ports[proposedPortKey];

	if (transition.type === 'direct') {
		return {
			...transition,
			[side]: proposedPort.statusId,
			[`${side}Index`]: proposedPort.portIndex,
		};
	}

	// transition.type === 'shared'
	if (side === 'source') {
		const originalPort = ports[originalPortKey];

		const updatedLinks = transition.links.map((link) =>
			link.source === originalPort.statusId && link.sourceIndex === originalPort.portIndex
				? {
						...link,
						source: proposedPort.statusId,
						sourceIndex: proposedPort.portIndex,
					}
				: link,
		);

		return {
			...transition,
			links: updatedLinks,
		};
	}

	// side === 'target'
	const originalPort = ports[originalPortKey];

	let didRerouteTarget = false;
	const updatedLinks = transition.links.map((link) => {
		if (proposedPort.statusId !== originalPort.statusId) {
			if (link.targetIndex === originalPort.portIndex) {
				return { ...link, targetIndex: proposedPort.portIndex };
			}

			// trigger best guess
			return { ...link, targetIndex: undefined };
		}

		if (link.targetIndex === originalPort.portIndex && !didRerouteTarget) {
			// reroute only the first matching link
			didRerouteTarget = true;
			return { ...link, targetIndex: proposedPort.portIndex };
		}

		return link;
	});

	return {
		...transition,
		target: proposedPort.statusId,
		links: updatedLinks,
	};
}
